import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, query, getDocs, doc, updateDoc, deleteDoc, orderBy, where } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';

export const TeacherDashboard = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [reviewReports, setReviewReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('reports');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const rSnap = await getDocs(query(collection(db, 'reports'), orderBy('createdAt', 'desc')));
      setReports(rSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'user' })));
      
      const rrSnap = await getDocs(query(collection(db, 'reviewReports'), orderBy('createdAt', 'desc')));
      setReviewReports(rrSnap.docs.map(d => ({ id: d.id, ...d.data(), type: 'review' })));
      
      const uSnap = await getDocs(collection(db, 'users'));
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { 
      console.error('Fetch error:', err); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleAction = async (reportId, userId, action, reportType, reviewId = null) => {
    if (!userId) return alert('User ID missing');
    
    const actionLabels = { warn: 'Warn', ban: 'Ban', resolve: 'Resolve' };
    const reason = prompt(`Enter reason for ${actionLabels[action].toLowerCase()}:`);
    if (!reason && action !== 'resolve') return;

    try {
      const userRef = doc(db, 'users', userId);

      if (action === 'ban') {
        await updateDoc(userRef, {
          isBanned: true,
          isWarned: false,
          warnings: `Banned: ${reason}`,
          warningCount: 0,
          bannedAt: new Date()
        });
      } else if (action === 'warn') {
        await updateDoc(userRef, {
          isWarned: true,
          warnings: reason,
          warningCount: 1,
          isBanned: false
        });
      }

      if (reportType === 'review' && reviewId) {
        try {
          await deleteDoc(doc(db, 'reviews', reviewId));
        } catch (delErr) {
          console.error('Could not delete review:', delErr.message);
        }
      }

      if (reportId) {
        const collectionName = reportType === 'review' ? 'reviewReports' : 'reports';
        await updateDoc(doc(db, collectionName, reportId), { 
          status: action === 'resolve' ? 'resolved' : action === 'warn' ? 'warned' : 'banned',
          actionedBy: user.uid,
          actionedAt: new Date(),
          actionReason: reason
        });
      }

      alert(`✅ User ${action}ed successfully!`);
      fetchData();
    } catch (err) {
      console.error('Action failed:', err);
      alert('❌ Error: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || 'pending').toLowerCase();
    const badges = {
      'pending': { text: 'PENDING', color: '#f59e0b' },
      'resolved': { text: 'RESOLVED', color: '#10b981' },
      'warned': { text: 'WARNED', color: '#f59e0b' },
      'banned': { text: 'BANNED', color: '#ef4444' }
    };
    return badges[s] || badges['pending'];
  };

  if (loading) return <p>Loading dashboard...</p>;

  const activeUsers = users.filter(u => !u.isBanned);
  const bannedUsers = users.filter(u => u.isBanned);

  return (
    <div className="teacher-dashboard">
      <h1>🏫 Teacher Dashboard</h1>
      <div className="dashboard-tabs">
        <button className={activeTab === 'reports' ? 'active' : ''} onClick={() => setActiveTab('reports')}>
          🚩 Reports ({reports.filter(r => (r.status||'pending') === 'pending').length})
        </button>
        <button className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>
          📝 Review Reports ({reviewReports.filter(r => (r.status||'pending') === 'pending').length})
        </button>
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
          👥 Users ({activeUsers.length} active, {bannedUsers.length} banned)
        </button>
      </div>

      {activeTab === 'reports' && (
        <div className="reports-section">
          <h2>User Reports</h2>
          {reports.length === 0 ? <p>No reports</p> : reports.map(r => {
            const badge = getStatusBadge(r.status);
            return (
              <Card key={r.id} className="report-item">
                <div className="report-header">
                  <div style={{flex:1}}>
                    <p><strong>Reported by:</strong> {r.reporterName || 'Unknown'}</p>
                    {r.reportedUserName && <p><strong>Reported user:</strong> {r.reportedUserName}</p>}
                    <p><strong>Reason:</strong> {r.reason || 'N/A'}</p>
                    <p><strong>Description:</strong> {r.description || 'N/A'}</p>
                    {r.attachments && r.attachments.length > 0 && (
                      <div style={{marginTop:'0.5rem'}}>
                        <strong>Attachments:</strong>
                        <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', marginTop:'0.25rem'}}>
                          {r.attachments.map((file, idx) => (
                            <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" 
                               style={{background:'#f3f4f6', padding:'4px 8px', borderRadius:'4px', fontSize:'0.8rem', textDecoration:'none', color:'#666'}}>
                              📎 {file.name || 'File ' + (idx+1)}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.actionedBy && <p style={{fontSize:'0.8rem', color:'#666', marginTop:'0.5rem'}}>Actioned by teacher</p>}
                  </div>
                  <span className="status-badge" style={{background: badge.color, color:'white', padding:'0.4rem 0.8rem', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'600'}}>
                    {badge.text}
                  </span>
                </div>
                {(r.status || 'pending') === 'pending' && r.reportedUserId && (
                  <div className="report-actions">
                    <Button onClick={() => handleAction(r.id, r.reportedUserId, 'warn', 'user')} className="btn-warning">⚠️ Warn</Button>
                    <Button onClick={() => handleAction(r.id, r.reportedUserId, 'ban', 'user')} className="btn-danger">🚫 Ban</Button>
                    <Button onClick={() => handleAction(r.id, r.reportedUserId, 'resolve', 'user')} className="btn-success">✅ Resolve</Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="reports-section">
          <h2>Review Reports</h2>
          {reviewReports.length === 0 ? <p>No review reports</p> : reviewReports.map(r => {
            const badge = getStatusBadge(r.status);
            return (
              <Card key={r.id} className="report-item">
                <div className="report-header">
                  <div style={{flex:1}}>
                    <p><strong>Reported by:</strong> {r.reporterName || 'Unknown'}</p>
                    <p><strong>Target:</strong> {r.reviewerName || 'Unknown'}</p>
                    <p><strong>Reason:</strong> {r.reason || 'N/A'}</p>
                    {r.attachments && r.attachments.length > 0 && (
                      <div style={{marginTop:'0.5rem'}}>
                        <strong>Attachments:</strong>
                        <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap', marginTop:'0.25rem'}}>
                          {r.attachments.map((file, idx) => (
                            <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" 
                               style={{background:'#f3f4f6', padding:'4px 8px', borderRadius:'4px', fontSize:'0.8rem', textDecoration:'none', color:'#666'}}>
                              📎 {file.name || 'File ' + (idx+1)}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.actionedBy && <p style={{fontSize:'0.8rem', color:'#666', marginTop:'0.5rem'}}>Actioned by teacher</p>}
                  </div>
                  <span className="status-badge" style={{background: badge.color, color:'white', padding:'0.4rem 0.8rem', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'600'}}>
                    {badge.text}
                  </span>
                </div>
                {(r.status || 'pending') === 'pending' && r.reviewerId && (
                  <div className="report-actions">
                    <Button onClick={() => handleAction(r.id, r.reviewerId, 'warn', 'review', r.reviewId)} className="btn-warning">⚠️ Warn & Delete</Button>
                    <Button onClick={() => handleAction(r.id, r.reviewerId, 'ban', 'review', r.reviewId)} className="btn-danger">🚫 Ban & Delete</Button>
                    <Button onClick={() => handleAction(r.id, r.reviewerId, 'resolve', 'review', r.reviewId)} className="btn-success">✅ Keep</Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-section">
          <h2>Active Users ({activeUsers.length})</h2>
          {activeUsers.length === 0 ? <p>No active users</p> : activeUsers.map(u => (
            <Card key={u.id} className="user-item">
              <h3>{u.displayName || 'Unknown'}</h3>
              <p>{u.email}</p>
              <p>Points: {u.points || 0}</p>
              {u.isWarned && <span className="warning-badge" style={{background:'#f59e0b', color:'white', padding:'4px 8px', borderRadius:'12px', fontSize:'12px'}}>⚠️ Warned</span>}
              {!u.isTeacher && (
                <div className="user-actions">
                  <Button onClick={() => handleAction(null, u.id, 'warn', 'user')} className="btn-warning">⚠️ Warn</Button>
                  <Button onClick={() => handleAction(null, u.id, 'ban', 'user')} className="btn-danger">🚫 Ban</Button>
                </div>
              )}
            </Card>
          ))}
          
          {bannedUsers.length > 0 && (
            <>
              <h2 style={{marginTop:'2rem', color:'#ef4444'}}>Banned Users ({bannedUsers.length})</h2>
              {bannedUsers.map(u => (
                <Card key={u.id} className="user-item" style={{opacity:0.6, border:'2px solid #ef4444'}}>
                  <h3>{u.displayName || 'Unknown'} <span style={{color:'#ef4444', fontSize:'0.875rem'}}>🚫 BANNED</span></h3>
                  <p>{u.email}</p>
                  {u.warnings && <p style={{fontSize:'0.875rem', color:'#666'}}>Reason: {u.warnings}</p>}
                </Card>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
